/**
 * Screener results, through the worker.
 *
 * Mirrors `ScreenerResults` in quantist_api's web_contract.py. Same boundary
 * rules as every other client here: only `VITE_QUANTIST_API_BASE` is called,
 * the API key lives in the worker, and no hosted API hostname appears.
 *
 * Unlike the catalogues, this does NOT fall back to a fixture. A catalogue
 * only decides what the dropdown offers; a screener result IS the answer, and
 * inventing one would be inventing a market call. A failure surfaces.
 */
import { apiBaseUrl, ChartRequestError, type AnalysisMethod } from "./client.ts";

/** One ranked stock. Numbers are numbers; the UI formats them. */
export interface ScreenerRow {
	/** The screener's own ordering, preserved by the API. Never re-derived here. */
	rank: number;
	code: string;
	display_name: string;
	close: number | null;
	money_flow: number | null;
	proportion: number | null;
	price_correlation: number | null;
	vwap: number | null;
	/** Per-criterion columns the shared fields do not cover. */
	extras: Record<string, number | string | boolean | null>;
}

export interface ScreenerResults {
	schema_version: string;
	slug: string;
	method: AnalysisMethod;
	period: { start: string | null; end: string | null; bars: number | null };
	count: number;
	rows: ScreenerRow[];
	meta: { schema_version: string; generated_at: string; source: string };
}

export interface ScreenerSource {
	baseUrl?: string | null;
	fetchImpl?: typeof fetch;
	signal?: AbortSignal;
	/** Per-attempt ceiling. Injected by tests; production uses the constant. */
	timeoutMs?: number;
	/** Backoff base. Injected by tests so they do not sit out a real wait. */
	retryDelayMs?: number;
}

/**
 * One attempt's ceiling.
 *
 * A cold `/screener/vwap_around?method=foreign` was measured at 17.6s — Cloud
 * Run cold start plus the query — and the next hit at under 0.1s. The measured
 * production miss can reach roughly 75s when the request also has to compute
 * the criterion, so the browser must not abort at the old 45s ceiling. 120s
 * leaves room for that first miss without approaching the 900s origin limit.
 */
export const SCREENER_TIMEOUT_MS = 120_000;
/** Per user action, sequential. Enough to ride out one cold start, not a storm. */
export const SCREENER_MAX_ATTEMPTS = 3;
/** Exponential base: waits 1s, then 2s. */
export const SCREENER_RETRY_BASE_MS = 1_000;
/**
 * Statuses worth a second try.
 *
 * These three are the gateway saying it could not reach or wait for the API.
 * Every other status is the API's own answer — a 404 is still a 404 on the
 * third ask, and retrying a 500 just asks the origin to fail again.
 */
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

function abortError(): DOMException {
	return new DOMException("Screener request was cancelled.", "AbortError");
}

/** One signal that fires when either does. `AbortSignal.any` is not in this TS lib. */
function anySignal(a: AbortSignal, b: AbortSignal): AbortSignal {
	const controller = new AbortController();
	if (a.aborted || b.aborted) return AbortSignal.abort();
	const stop = () => controller.abort();
	a.addEventListener("abort", stop, { once: true });
	b.addEventListener("abort", stop, { once: true });
	return controller.signal;
}

/** A cancellable wait. A navigation must not be held up by a pending backoff. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
	if (ms <= 0) return Promise.resolve();
	return new Promise((resolve, reject) => {
		const onAbort = () => {
			clearTimeout(timer);
			reject(abortError());
		};
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}

function assertResults(value: unknown): asserts value is ScreenerResults {
	const payload = value as Partial<ScreenerResults> | null;
	if (!payload || typeof payload !== "object") throw new ChartRequestError("Screener payload is not an object.");
	if (!Array.isArray(payload.rows)) throw new ChartRequestError("Screener payload has no rows array.");
	for (const row of payload.rows) {
		if (typeof row?.code !== "string" || typeof row?.rank !== "number") {
			throw new ChartRequestError("A screener row is missing its rank or code.");
		}
	}
}

/**
 * One criterion, one method.
 *
 * Both are in the URL because they are two different analyses of the same
 * criterion, and the worker keys its cache on exactly this pair.
 *
 * Retries exist for one measured failure: the first request for a criterion
 * pays a Cloud Run cold start plus the query — 17.6s for vwap_around/foreign
 * — and the next one lands in under 0.1s. What the second attempt gets is a
 * *warm Cloud Run instance*; it may also get a warm cache, but not reliably,
 * because a client disconnect can cancel the worker before it writes one.
 * Either way the second ask is the cheap one, so a transport failure or a
 * 502/503/504 is worth up to three attempts, one at a time.
 *
 * What retrying does NOT fix: if the origin needs longer than Cloudflare will
 * hold a request open, every attempt dies at the same wall and more of them
 * change nothing. That is what the EOD precompute is for; see
 * docs/eod-cache-contract.md. The error below says so rather than pretending
 * otherwise.
 */
export async function fetchScreenerResults(
	slug: string,
	method: AnalysisMethod,
	source: ScreenerSource = {},
): Promise<ScreenerResults> {
	const baseUrl = source.baseUrl === undefined ? apiBaseUrl() : source.baseUrl;
	if (!baseUrl) throw new ChartRequestError("No API base URL is configured, so screener results cannot be loaded.");

	const request = source.fetchImpl ?? fetch;
	const url = `${baseUrl.replace(/\/+$/, "")}/screener/${encodeURIComponent(slug)}?method=${method}`;
	const timeoutMs = source.timeoutMs ?? SCREENER_TIMEOUT_MS;
	const backoffMs = source.retryDelayMs ?? SCREENER_RETRY_BASE_MS;

	let lastReason = "it did not respond";

	for (let attempt = 1; attempt <= SCREENER_MAX_ATTEMPTS; attempt += 1) {
		if (source.signal?.aborted) throw abortError();
		// Sequential by construction: the next attempt cannot start until this
		// wait resolves, so a slow origin never gets three requests at once.
		if (attempt > 1) await sleep(backoffMs * 2 ** (attempt - 2), source.signal);

		const timeout = AbortSignal.timeout(timeoutMs);
		const signal = source.signal ? anySignal(source.signal, timeout) : timeout;

		let response: Response;
		try {
			response = await request(url, { headers: { accept: "application/json" }, signal });
		} catch (cause) {
			// A navigation is not a failure to report — the caller is gone.
			if (source.signal?.aborted) throw abortError();
			lastReason = timeout.aborted
				? `it did not respond within ${Math.round(timeoutMs / 1000)}s`
				: `the connection failed (${(cause as Error).message})`;
			continue;
		}

		if (RETRYABLE_STATUSES.has(response.status)) {
			lastReason = `the gateway returned ${response.status}`;
			continue;
		}

		// Any other non-2xx is the API's own answer. Asking again would only
		// produce the same one.
		if (!response.ok) throw new ChartRequestError(`Screener "${slug}" returned ${response.status}.`, response.status);

		let payload: unknown;
		try {
			payload = await response.json();
		} catch {
			throw new ChartRequestError(`Screener "${slug}" returned a body that is not JSON.`);
		}
		// Rows arrive exactly as sent: every one of them, in the API's order,
		// with the rank it assigned. Nothing here re-sorts, trims or renumbers.
		assertResults(payload);
		return payload;
	}

	throw new ChartRequestError(
		`Could not load "${slug}" after ${SCREENER_MAX_ATTEMPTS} attempts: ${lastReason}. ` +
			"A criterion asked for the first time is computed on demand and may still be warming — " +
			"results are precomputed after the market close. Try again in a moment.",
	);
}
